import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { cn } from '@mdvip/shared/lib/utils';

interface NetworkConfig {
  /** Tailwind text color carrying the network's own brand color. */
  fg: string;
  /** FA brands glyph class, e.g. "fa-cc-visa". */
  icon: string;
}

/**
 * Card-network brand colors, applied to the mark itself rather than to a filled ground.
 * Written as literal utility strings so Tailwind's source scanner picks them up.
 */
const NETWORK_CONFIG: Record<string, NetworkConfig> = {
  VISA: { fg: 'text-[#1434CB]', icon: 'fa-cc-visa' },
  MC: { fg: 'text-[#1A1A2E]', icon: 'fa-cc-mastercard' },
  AMEX: { fg: 'text-[#1F72CF]', icon: 'fa-cc-amex' },
  DISC: { fg: 'text-[#FF6000]', icon: 'fa-cc-discover' },
};

export interface NetworkChipProps {
  kind?: 'card' | 'bank';
  /** Card network code (e.g. "VISA", "MC", "AMEX", "DISC"). Ignored for bank kind. */
  network?: string;
  /**
   * `outline` draws a neutral 1.5px border with the mark in the network's brand color;
   * `bare` drops the border and renders the mark alone, for dense or low-emphasis lists.
   */
  variant?: 'outline' | 'bare';
  className?: string;
}

/**
 * NetworkChip: a 48×32 payment method chip. A card-network logo or a bank landmark glyph, drawn in
 * the network's own brand color inside a neutral outline. Used inside payment method row
 * components and the saved-method tile.
 */
function NetworkChip({ kind = 'card', network, variant = 'outline', className }: NetworkChipProps) {
  const base = cn(
    'flex h-8 w-12 shrink-0 items-center justify-center rounded-md',
    variant === 'outline' && 'border-[1.5px] border-border',
    className,
  );

  if (kind === 'bank') {
    return (
      // Slate mark, matching the hex the design system hardcodes for the bank landmark.
      <div className={cn(base, 'text-[#475569]')}>
        <Icon name="landmark" className="text-[20px]" />
      </div>
    );
  }

  const config = NETWORK_CONFIG[network ?? ''];
  return (
    <div className={cn(base, config ? config.fg : 'text-foreground')}>
      {config ? (
        <i className={cn('fa-brands', config.icon, 'text-[26px] leading-none')} aria-hidden="true" />
      ) : (
        <span className="text-[10px] font-bold tracking-tight">{network}</span>
      )}
    </div>
  );
}

export { NetworkChip };
